/**
 * 회원 목록 페이지
 *
 * 전체 회원을 테이블로 표시하며 검색·필터·정렬·페이지네이션을 지원한다.
 * 버전(V1/V2) 탭으로 먼저 범위를 좁힌 뒤, 상태(블랙리스트·프리미엄) 필터와
 * 텍스트 검색을 조합할 수 있다. 가로 스크롤 힌트 화살표는 overflow 여부를 실시간 감지한다.
 *
 * 상태 영속화: useSearchParams 기반 URL 쿼리 파라미터
 *   ?q=검색어&ver=V2&status=blacklist&sort=id&dir=desc&page=1
 *   → 새로고침·뒤로가기 후에도 필터·정렬·페이지 유지됨
 *
 * 블랙리스트 배지 클릭 → 확인 모달 → PATCH /api/admin/user/{id}/blacklist 즉시 토글
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { userService } from "../../services/userService";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { BaeringUser } from "../../types/user";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorAlert from "../../components/ui/ErrorAlert";
import { formatDate } from "../../utils/format";
import MemberSubNav from "../../components/admin/MemberSubNav";

type VersionFilter = "all" | "V1" | "V2";
type SortField = "id" | "userId" | "deviceAddress" | "phoneNumber" | "createdDt" | "updatedDt";
type SortDirection = "asc" | "desc";

/** 블랙리스트 토글 확인 모달에 넘길 컨텍스트 */
type BlacklistModal = {
  user: BaeringUser;
  field: "device" | "phone";
} | null;

const ITEMS_PER_PAGE = 50;

export default function MemberList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // ─── URL 파라미터에서 상태 읽기 (기본값 포함) ────────────────────────────
  const searchTerm    = searchParams.get("q")      ?? "";
  const filterStatus  = (searchParams.get("status") ?? "all") as "all" | "blacklist" | "premium";
  const versionFilter = (searchParams.get("ver")    ?? "all") as VersionFilter;
  const sortField     = (searchParams.get("sort")   ?? "id")  as SortField;
  const sortDirection = (searchParams.get("dir")    ?? "desc") as SortDirection;
  const currentPage   = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10));

  // UI 전용 상태 (URL에 저장 불필요)
  const [showLeftScroll, setShowLeftScroll]   = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(true);
  const [blacklistModal, setBlacklistModal]   = useState<BlacklistModal>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // ─── URL 파라미터 업데이트 헬퍼 ─────────────────────────────────────────
  const updateParams = useCallback(
    (updates: Record<string, string | null | undefined>, resetPage = false) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(updates)) {
            if (v == null || v === "" || v === "all" || (k === "sort" && v === "id") || (k === "dir" && v === "desc")) {
              next.delete(k);
            } else {
              next.set(k, v);
            }
          }
          if (resetPage) next.delete("page");
          return next;
        },
        { replace: true }  // 필터 변경은 history를 쌓지 않음 (뒤로가기 = 이전 페이지)
      );
    },
    [setSearchParams]
  );

  const setSearchTerm    = (v: string)           => updateParams({ q: v }, true);
  const setFilterStatus  = (v: typeof filterStatus)  => updateParams({ status: v }, true);
  const setVersionFilter = (v: VersionFilter)    => updateParams({ ver: v }, true);
  const setCurrentPage   = (page: number)        => updateParams({ page: page === 1 ? null : String(page) });

  // ─── 데이터 쿼리 ────────────────────────────────────────────────────────
  const { data: users, isLoading, error } = useQuery({
    queryKey: ["users"],
    queryFn: userService.getAllUsers,
  });

  const blacklistMutation = useMutation({
    mutationFn: ({
      id,
      isBlacklist,
      isBlacklistPhone,
    }: {
      id: number;
      isBlacklist: boolean;
      isBlacklistPhone: boolean;
    }) => userService.updateBlacklist(id, isBlacklist, isBlacklistPhone),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setBlacklistModal(null);
    },
  });

  const handleBlacklistConfirm = () => {
    if (!blacklistModal) return;
    const { user, field } = blacklistModal;
    blacklistMutation.mutate({
      id: user.id,
      isBlacklist: field === "device" ? !user.isBlacklist : user.isBlacklist,
      isBlacklistPhone: field === "phone" ? !user.isBlacklistPhone : user.isBlacklistPhone,
    });
  };

  const handleSort = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        updateParams({ dir: sortDirection === "asc" ? "desc" : "asc" });
      } else {
        updateParams({ sort: field, dir: "asc" });
      }
    },
    [sortField, sortDirection, updateParams]
  );

  // ─── 필터링 & 정렬 ──────────────────────────────────────────────────────
  const filteredAndSortedUsers = useMemo(() => {
    if (!users) return [];
    let filtered = users.filter((user) => {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        (user.userId ?? "").toLowerCase().includes(term) ||
        (user.phoneNumber ?? "").includes(searchTerm) ||
        (user.deviceAddress ?? "").toLowerCase().includes(term);
      const matchesFilter =
        filterStatus === "all" ||
        (filterStatus === "blacklist" && (user.isBlacklist || user.isBlacklistPhone)) ||
        (filterStatus === "premium" && user.isAddFunction);
      const matchesVersion = versionFilter === "all" || user.deviceVersion === versionFilter;
      return matchesSearch && matchesFilter && matchesVersion;
    });

    filtered.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;
      switch (sortField) {
        case "id":            aValue = a.id; bValue = b.id; break;
        case "userId":        aValue = (a.userId ?? "").toLowerCase(); bValue = (b.userId ?? "").toLowerCase(); break;
        case "deviceAddress": aValue = (a.deviceAddress ?? "").toLowerCase(); bValue = (b.deviceAddress ?? "").toLowerCase(); break;
        case "phoneNumber":   aValue = a.phoneNumber ?? ""; bValue = b.phoneNumber ?? ""; break;
        case "createdDt":     aValue = new Date(a.createdDt).getTime(); bValue = new Date(b.createdDt).getTime(); break;
        case "updatedDt":     aValue = new Date(a.updatedDt).getTime(); bValue = new Date(b.updatedDt).getTime(); break;
        default:              return 0;
      }
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return filtered;
  }, [users, searchTerm, filterStatus, versionFilter, sortField, sortDirection]);

  const totalPages = Math.ceil(filteredAndSortedUsers.length / ITEMS_PER_PAGE);
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredAndSortedUsers.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredAndSortedUsers, currentPage]);

  const versionStats = useMemo(() => {
    if (!users) return { all: 0, V1: 0, V2: 0 };
    return {
      all: users.length,
      V1: users.filter((u) => u.deviceVersion === "V1").length,
      V2: users.filter((u) => u.deviceVersion === "V2").length,
    };
  }, [users]);

  // ─── 가로 스크롤 힌트 ───────────────────────────────────────────────────
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setShowLeftScroll(scrollLeft > 10);
    setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 10);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    handleScroll();
    container.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [handleScroll]);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorAlert />;

  const sortIcon = (field: SortField) =>
    sortField === field
      ? sortDirection === "asc"
        ? "fa-arrow-up text-primary"
        : "fa-arrow-down text-primary"
      : "fa-sort text-gray-400";

  const modalTitle = blacklistModal
    ? blacklistModal.field === "device"
      ? blacklistModal.user.isBlacklist ? "기기 블랙리스트 해제" : "기기 블랙리스트 등록"
      : blacklistModal.user.isBlacklistPhone ? "번호 블랙리스트 해제" : "번호 블랙리스트 등록"
    : "";

  const modalDesc = blacklistModal
    ? `${blacklistModal.user.userId ?? blacklistModal.user.id} 회원을 ${modalTitle}하시겠습니까?`
    : "";

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">회원 관리</h1>
        <p className="text-gray-500 mt-2">전체 회원 목록을 조회하고 관리합니다</p>
      </div>

      <MemberSubNav active="list" />

      {/* Version Filter Tabs */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">
            <i className="fas fa-mobile-alt mr-2 text-primary"></i>
            버전별 회원 현황
          </h2>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {([
            { key: "all", label: "전체", count: versionStats.all, activeClass: "bg-gradient-to-r from-primary to-[#5FA060]" },
            { key: "V1",  label: "V1",   count: versionStats.V1,  activeClass: "bg-gradient-to-r from-blue-500 to-blue-600" },
            { key: "V2",  label: "V2",   count: versionStats.V2,  activeClass: "bg-gradient-to-r from-purple-500 to-purple-600" },
          ] as const).map(({ key, label, count, activeClass }) => (
            <button
              key={key}
              onClick={() => setVersionFilter(key)}
              className={`p-4 rounded-lg font-medium transition-all ${
                versionFilter === key
                  ? `${activeClass} text-white shadow-md transform scale-105`
                  : "bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200"
              }`}
            >
              <div className="text-sm opacity-90">{label}</div>
              <div className="text-2xl font-bold mt-1">{count}</div>
              <div className="text-xs opacity-75 mt-1">명</div>
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <i className="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              placeholder="아이디, 연락처, 기기 주소로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="flex space-x-2">
            {([
              { key: "all",       label: "전체",      active: "bg-primary",       inactive: "bg-gray-100 text-gray-600 hover:bg-gray-150" },
              { key: "blacklist", label: "블랙리스트", active: "bg-text-red",      inactive: "bg-gray-100 text-gray-600 hover:bg-gray-150" },
              { key: "premium",   label: "프리미엄",   active: "bg-marker-orange", inactive: "bg-gray-100 text-gray-600 hover:bg-gray-150" },
            ] as const).map(({ key, label, active, inactive }) => (
              <button
                key={key}
                onClick={() => setFilterStatus(key)}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                  filterStatus === key ? `${active} text-white shadow-md` : inactive
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-4 text-sm text-gray-500">
          총 <span className="font-semibold text-primary">{filteredAndSortedUsers.length}</span>명의 회원
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden relative">
        {showLeftScroll && (
          <div className="absolute top-0 left-0 h-full w-16 bg-gradient-to-r from-white via-white/80 to-transparent pointer-events-none z-10 flex items-center justify-start pl-2">
            <div className="bg-white/90 rounded-full p-2 shadow-md">
              <i className="fas fa-chevron-left text-gray-500"></i>
            </div>
          </div>
        )}
        {showRightScroll && (
          <div className="absolute top-0 right-0 h-full w-16 bg-gradient-to-l from-white via-white/80 to-transparent pointer-events-none z-10 flex items-center justify-end pr-2">
            <div className="bg-white/90 rounded-full p-2 shadow-md">
              <i className="fas fa-chevron-right text-gray-500"></i>
            </div>
          </div>
        )}

        <div className="overflow-x-auto" ref={scrollContainerRef}>
          <table className="min-w-full divide-y divide-gray-150">
            <thead className="bg-gradient-to-r from-primary-lighter to-bg-light">
              <tr>
                {([
                  { label: "번호",            field: "id",            minW: "min-w-[100px]", sortable: true  },
                  { label: "안드로이드 아이디", field: "userId",        minW: "min-w-[180px]", sortable: true  },
                  { label: "기기 주소값",       field: "deviceAddress", minW: "min-w-[200px]", sortable: true  },
                  { label: "연락처",            field: "phoneNumber",   minW: "min-w-[150px]", sortable: true  },
                  { label: "버전",              field: null,            minW: "min-w-[100px]", sortable: false },
                  { label: "블랙(기기)",        field: null,            minW: "min-w-[130px]", sortable: false },
                  { label: "블랙(번호)",        field: null,            minW: "min-w-[130px]", sortable: false },
                  { label: "추가기능",          field: null,            minW: "min-w-[120px]", sortable: false },
                  { label: "메모",              field: null,            minW: "min-w-[150px]", sortable: false },
                  { label: "가입일",            field: "createdDt",     minW: "min-w-[150px]", sortable: true  },
                ] as const).map(({ label, field, minW, sortable }) => (
                  <th
                    key={label}
                    className={`px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider ${minW} ${
                      sortable ? "cursor-pointer hover:bg-primary-lighter/50 transition-colors" : ""
                    }`}
                    onClick={sortable && field ? () => handleSort(field as SortField) : undefined}
                  >
                    <div className="flex items-center justify-center space-x-1 whitespace-nowrap">
                      <span>{label}</span>
                      {sortable && field && <i className={`fas ${sortIcon(field as SortField)}`}></i>}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-150">
              {paginatedUsers.length > 0 ? (
                paginatedUsers.map((user) => (
                  <tr
                    key={user.id}
                    onClick={() => navigate(`/admin/member/${user.id}`)}
                    className="hover:bg-primary-lighter transition-colors group cursor-pointer"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="text-primary group-hover:text-[#5FA060] font-semibold">#{user.id}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="text-sm font-medium text-gray-900">{user.userId}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <code className="text-xs bg-gray-50 px-2 py-1 rounded text-gray-600">{user.deviceAddress}</code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                      <i className="fas fa-phone text-gray-400 mr-2"></i>{user.phoneNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${
                        user.deviceVersion === "V1" ? "bg-blue-50 text-blue-600 border border-blue-200"
                          : user.deviceVersion === "V2" ? "bg-purple-50 text-purple-600 border border-purple-200"
                          : "bg-gray-50 text-gray-600 border border-gray-200"
                      }`}>
                        <i className="fas fa-mobile-alt mr-1"></i>{user.deviceVersion}
                      </span>
                    </td>

                    {/* 블랙(기기) */}
                    <td
                      className="px-6 py-4 whitespace-nowrap text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => setBlacklistModal({ user, field: "device" })}
                        title="클릭하여 블랙리스트 전환"
                        className={`px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full transition-all hover:opacity-80 hover:scale-105 ${
                          user.isBlacklist
                            ? "bg-text-red/10 text-text-red border border-text-red/30"
                            : "bg-primary/10 text-primary border border-primary/30"
                        }`}
                      >
                        {user.isBlacklist
                          ? <><i className="fas fa-ban mr-1"></i>블랙</>
                          : <><i className="fas fa-check mr-1"></i>일반</>}
                      </button>
                    </td>

                    {/* 블랙(번호) */}
                    <td
                      className="px-6 py-4 whitespace-nowrap text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => setBlacklistModal({ user, field: "phone" })}
                        title="클릭하여 블랙리스트 전환"
                        className={`px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full transition-all hover:opacity-80 hover:scale-105 ${
                          user.isBlacklistPhone
                            ? "bg-text-red/10 text-text-red border border-text-red/30"
                            : "bg-primary/10 text-primary border border-primary/30"
                        }`}
                      >
                        {user.isBlacklistPhone
                          ? <><i className="fas fa-ban mr-1"></i>블랙</>
                          : <><i className="fas fa-check mr-1"></i>일반</>}
                      </button>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${
                        user.isAddFunction
                          ? "bg-marker-orange/10 text-marker-orange border border-marker-orange/30"
                          : "bg-gray-100 text-gray-600 border border-gray-200"
                      }`}>
                        {user.isAddFunction
                          ? <><i className="fas fa-star mr-1"></i>활성</>
                          : <><i className="fas fa-minus-circle mr-1"></i>비활성</>}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600 max-w-xs truncate">
                      {user.memo && user.memo.length > 20 ? `${user.memo.substring(0, 20)}...` : user.memo || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                      <i className="fas fa-calendar-alt mr-2"></i>
                      {formatDate(user.createdDt)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center text-gray-500">
                    <i className="fas fa-search text-4xl mb-4 text-gray-300 block"></i>
                    <p>검색 결과가 없습니다</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-150 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span className="font-medium">{filteredAndSortedUsers.length}</span>개 중{" "}
                <span className="font-medium">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span>-
                <span className="font-medium">{Math.min(currentPage * ITEMS_PER_PAGE, filteredAndSortedUsers.length)}</span>개 표시
              </div>
              <div className="flex items-center space-x-2">
                {([
                  { label: <i className="fas fa-angle-double-left"></i>, page: 1,               disabled: currentPage === 1 },
                  { label: <i className="fas fa-angle-left"></i>,        page: currentPage - 1, disabled: currentPage === 1 },
                ] as const).map(({ label, page, disabled }, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(page)}
                    disabled={disabled}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      disabled ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-white text-gray-700 hover:bg-primary hover:text-white shadow-sm"
                    }`}
                  >
                    {label}
                  </button>
                ))}

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5)                    pageNum = i + 1;
                  else if (currentPage <= 3)              pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else                                    pageNum = currentPage - 2 + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        currentPage === pageNum
                          ? "bg-gradient-to-r from-primary to-[#5FA060] text-white shadow-md"
                          : "bg-white text-gray-700 hover:bg-gray-100 shadow-sm"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                {([
                  { label: <i className="fas fa-angle-right"></i>,        page: currentPage + 1, disabled: currentPage === totalPages },
                  { label: <i className="fas fa-angle-double-right"></i>, page: totalPages,      disabled: currentPage === totalPages },
                ] as const).map(({ label, page, disabled }, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(page)}
                    disabled={disabled}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      disabled ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-white text-gray-700 hover:bg-primary hover:text-white shadow-sm"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 블랙리스트 토글 확인 모달 */}
      {blacklistModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-80 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                (blacklistModal.field === "device" ? blacklistModal.user.isBlacklist : blacklistModal.user.isBlacklistPhone)
                  ? "bg-green-50" : "bg-red-50"
              }`}>
                <i className={`fas ${
                  (blacklistModal.field === "device" ? blacklistModal.user.isBlacklist : blacklistModal.user.isBlacklistPhone)
                    ? "fa-circle-check text-green-500" : "fa-ban text-text-red"
                }`}></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900">{modalTitle}</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">{modalDesc}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setBlacklistModal(null)}
                disabled={blacklistMutation.isPending}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
              >
                취소
              </button>
              <button
                onClick={handleBlacklistConfirm}
                disabled={blacklistMutation.isPending}
                className={`flex-1 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-50 ${
                  (blacklistModal.field === "device" ? blacklistModal.user.isBlacklist : blacklistModal.user.isBlacklistPhone)
                    ? "bg-primary hover:bg-[#5FA060]" : "bg-text-red hover:opacity-90"
                }`}
              >
                {blacklistMutation.isPending ? "처리 중..." : "확인"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
